import React from 'react';
import { listenToHeightChanges } from 'wix-height-updater';
import { IProvidersProps } from './intefaces';
import { withWixSDKWidget } from '../../services/withWixSDK';
import {
  IWixSDKViewerEnvironmentContext,
  useEnvironment,
} from 'yoshi-flow-editor-runtime';
import { UserProvider } from '../User/UserProvider';
import { LocationProvider } from '../Location/LocationProvider';
import { ChallengeDataProvider } from '../ChallengeDataProvider/challengeDataProvider';
import { GeneralDataProvider } from '../GeneralDataProvider/GeneralDataProvider';
import { BIProvider } from '../BI/BIProvider';
import { ChallengesListDataProvider } from '../ChallengesListDataProvider/challengesListDataProvider';
import { ExperimentsProvider } from '../Experiments/ExperimentsContext';
import { InviteLinkProvider } from '../InviteLink/InviteLinkContext';
import { PaidPlansProvider } from '../PaidPlans/paidPlansContext';
import { ToastProvider } from '../ToastContext/ToastProvider';
import { ParticipantStepsDataProvider } from '../ParticipantStepsDataProvider/ParticipantStepsDataProvider';
import { ParticipantSectionsProvider } from '../ParticipantSections/ParticipantSectionsProvider';
import { ResolveStepDataProvider } from '../ResolveStep/ResolveStepDataProvider';
import { SettingsEventsProvider } from '../SettingsEvents/SettingsEvents';
import { IsStepHiddenProvider } from '../IsStepHidden/IsStepHiddenProvider';
import { LoadingProvider } from '../Loading/LoadingContext';
import { getIsFullWidth } from '../../selectors/platform';
import { LocalStorageProvider } from '../LocalStorage/LocalStorageProvider';

const Providers: React.FC<
  IProvidersProps &
    IWixSDKViewerEnvironmentContext & {
      Component: any;
    }
> = (props) => {
  const [isFullWidth, setIsFullWidth] = React.useState<boolean>(false);
  const { Component } = props;
  const { dimensions, isEditor } = useEnvironment();

  React.useEffect(() => {
    if (props.Wix) {
      (props.Wix as any).isFullWidth((isFW) => {
        if (isFullWidth !== isFW) {
          setIsFullWidth(isFW);
        }
      });
    }

    if (isEditor) {
      listenToHeightChanges(props.Wix, window);
    }

    props.screenOpen && props.screenOpen();
    props.reportSSRIsRendered && props.reportSSRIsRendered();
  }, []);

  return (
    <BIProvider
      ownerId={props.ownerId}
      beforeAndAfterAction={props.beforeAndAfterAction}
      afterAction={props.afterAction}
      screenOpen={props.screenOpen}
      tabOpen={props.tabOpen}
      startTimeOfRender={props.startTimeOfRender}
      reportSSRIsRendered={props.reportSSRIsRendered}
      memberWebAppButtonClick={props.memberWebAppButtonClick}
    >
      <GeneralDataProvider
        host={props.host}
        isFullWidthLayout={props.Wix ? isFullWidth : getIsFullWidth(dimensions)}
        instanceId={props.instanceId}
        instance={props.instance}
      >
        <LocationProvider
          path={props.path}
          currentLocation={props.currentLocation}
          basePath={props.basePath}
          query={props.query}
          location={props.location}
          goToPage={props.goToPage}
          goToExternalUrl={props.goToExternalUrl}
        >
          <LocalStorageProvider
            requestItem={props.requestItem}
            setItem={props.setItem}
            storage={props.storage}
          >
            <ExperimentsProvider
              experiments={props.experiments}
              disabled={props.disabled}
              enabled={props.enabled}
            >
              <UserProvider
                incrementParticipantsCompletedStepSummary={
                  props.incrementParticipantsCompletedStepSummary
                }
                updateParticipant={props.updateParticipant}
                user={props.user}
                userType={props.userType}
                participant={props.participant}
                promptLogin={props.promptLogin}
                join={props.join}
                cancelJoinRequest={props.cancelJoinRequest}
                leaveTheChallenge={props.leaveTheChallenge}
                userTypeHandlers={props.userTypeHandlers}
              >
                <ChallengeDataProvider
                  isGroupsInstalled={props.isGroupsInstalled}
                  challengeData={props.challengeData}
                >
                  <ChallengesListDataProvider
                    challengesListData={props.challengesListData}
                  >
                    <InviteLinkProvider inviteLink={props.inviteLink}>
                      <PaidPlansProvider
                        eligiblePlans={props.eligiblePlans}
                        userPaidPlans={props.userPaidPlans}
                      >
                        <ToastProvider
                          clearError={props.clearError}
                          errorMessageKey={props.errorMessageKey}
                        >
                          <ParticipantSectionsProvider
                            isListParticipantSectionsRequestInProgress={
                              props.isListParticipantSectionsRequestInProgress
                            }
                            listParticipantSections={
                              props.listParticipantSections
                            }
                            getParticipantSections={
                              props.getParticipantSections
                            }
                            updateParticipantSections={
                              props.updateParticipantSections
                            }
                          >
                            <ParticipantStepsDataProvider
                              participantSteps={props.participantSteps}
                              updateParticipantSteps={
                                props.updateParticipantSteps
                              }
                              isParticipantStepsLoading={
                                props.isParticipantStepsLoading
                              }
                              updateParticipantStepStatus={
                                props.updateParticipantStepStatus
                              }
                            >
                              <ResolveStepDataProvider
                                isResolveStepRequestInProgress={
                                  props.isResolveStepRequestInProgress
                                }
                                resolveStep={props.resolveStep}
                                resolveStepError={props.resolveStepError}
                              >
                                <SettingsEventsProvider
                                  buttonState={props.buttonState}
                                  pageRole={props.pageRole}
                                  listLayoutSelectedTab={
                                    props.shownTab ||
                                    props.listLayoutSelectedTab
                                  }
                                  shownTab={
                                    props.shownTab ||
                                    props.listLayoutSelectedTab
                                  }
                                >
                                  <IsStepHiddenProvider
                                    isStepHidden={props.isStepHidden}
                                    isVisibleStepLockedForComplete={
                                      props.isVisibleStepLockedForComplete
                                    }
                                  >
                                    <LoadingProvider
                                      showLoader={props.showLoader}
                                      hideLoader={props.hideLoader}
                                      isLoading={props.isLoading}
                                    >
                                      <Component {...props} />
                                    </LoadingProvider>
                                  </IsStepHiddenProvider>
                                </SettingsEventsProvider>
                              </ResolveStepDataProvider>
                            </ParticipantStepsDataProvider>
                          </ParticipantSectionsProvider>
                        </ToastProvider>
                      </PaidPlansProvider>
                    </InviteLinkProvider>
                  </ChallengesListDataProvider>
                </ChallengeDataProvider>
              </UserProvider>
            </ExperimentsProvider>
          </LocalStorageProvider>
        </LocationProvider>
      </GeneralDataProvider>
    </BIProvider>
  );
};

Providers.displayName = 'ChallengesProviders';

export function withProviders(ProvidedComponent) {
  return withWixSDKWidget(function (props: any) {
    return <Providers {...props} Component={ProvidedComponent} />;
  });
}
